/**
 * CommandPalette Component
 *
 * Global command palette activated via Cmd+K / Ctrl+K
 * Features:
 * - Quick navigation to all pages
 * - Fuzzy search with keyword matching
 * - Keyboard navigation (↑/↓/Enter/Esc)
 * - WCAG 2.1 AA compliant
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Command } from 'lucide-react';
import { clsx } from 'clsx';
import { useCommandPalette, type CommandItem } from '@/hooks/use-command-palette';

export function CommandPalette() {
  const { isOpen, close, filterCommands } = useCommandPalette();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter commands based on query
  const filteredCommands = filterCommands(query);

  // Group commands by category
  const groupedCommands = filteredCommands.reduce(
    (acc, cmd) => {
      if (!acc[cmd.category]) {
        acc[cmd.category] = [];
      }
      acc[cmd.category].push(cmd);
      return acc;
    },
    {} as Record<string, CommandItem[]>
  );

  // Flatten for keyboard navigation
  const flattenedCommands = filteredCommands;

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input after render
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [isOpen]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = listRef.current?.querySelector('[data-selected="true"]');
    selectedElement?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, flattenedCommands.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flattenedCommands[selectedIndex]) {
            flattenedCommands[selectedIndex].action(query);
          }
          break;
        case 'Escape':
          e.preventDefault();
          close();
          break;
      }
    },
    [flattenedCommands, selectedIndex, close, query]
  );

  // Execute command on click
  const handleCommandClick = useCallback(
    (cmd: CommandItem) => {
      cmd.action(query);
    },
    [query]
  );

  if (!isOpen) return null;

  const categoryLabels: Record<string, string> = {
    navigation: '페이지 이동',
    action: '빠른 작업',
    recent: '최근 항목',
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* Command Palette */}
      <div
        className="fixed inset-x-0 top-[20%] z-50 mx-auto w-full max-w-xl px-4"
        role="dialog"
        aria-modal="true"
        aria-label="명령 팔레트"
      >
        <div className="overflow-hidden rounded-xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
          {/* Search input */}
          <div className="flex items-center border-b border-gray-200 dark:border-gray-700 px-4">
            <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="페이지 또는 작업 검색..."
              className="h-14 w-full border-0 bg-transparent px-4 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-0"
              aria-label="명령 검색"
              aria-controls="command-list"
              aria-activedescendant={
                flattenedCommands[selectedIndex]
                  ? `command-${flattenedCommands[selectedIndex].id}`
                  : undefined
              }
            />
            <kbd className="hidden rounded bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 sm:block">
              ESC
            </kbd>
          </div>

          {/* Command list */}
          <div
            ref={listRef}
            id="command-list"
            role="listbox"
            className="max-h-[60vh] overflow-y-auto p-2"
          >
            {flattenedCommands.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                검색 결과가 없습니다
              </div>
            ) : (
              Object.entries(groupedCommands).map(([category, items]) => (
                <div key={category} className="mb-2">
                  {/* Category header */}
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {categoryLabels[category] || category}
                  </div>

                  {/* Commands */}
                  {items.map((cmd) => {
                    const globalIndex = flattenedCommands.findIndex((c) => c.id === cmd.id);
                    const isSelected = globalIndex === selectedIndex;
                    const Icon = cmd.icon;

                    return (
                      <button
                        key={cmd.id}
                        id={`command-${cmd.id}`}
                        role="option"
                        aria-selected={isSelected}
                        data-selected={isSelected}
                        onClick={() => handleCommandClick(cmd)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={clsx(
                          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                          isSelected
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        )}
                      >
                        <div
                          className={clsx(
                            'flex h-9 w-9 items-center justify-center rounded-lg',
                            isSelected ? 'bg-primary-100 dark:bg-primary-800/50' : 'bg-gray-100 dark:bg-gray-700'
                          )}
                        >
                          <Icon
                            className={clsx(
                              'h-5 w-5',
                              isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                            )}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{cmd.title}</div>
                          {cmd.subtitle && (
                            <div
                              className={clsx(
                                'text-sm truncate',
                                isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                              )}
                            >
                              {cmd.subtitle}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <kbd className="rounded bg-primary-100 dark:bg-primary-800/50 px-1.5 py-0.5 text-xs text-primary-600 dark:text-primary-400">
                            Enter
                          </kbd>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer with keyboard hints */}
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5">↑</kbd>
                <kbd className="rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5">↓</kbd>
                <span>이동</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5">Enter</kbd>
                <span>선택</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5">ESC</kbd>
                <span>닫기</span>
              </span>
            </div>
            <div className="flex items-center gap-1 text-gray-400 dark:text-gray-500">
              <Command className="h-3.5 w-3.5" />
              <span>K로 열기</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
